import { Outlet, createRootRouteWithContext, createRoute, redirect } from '@tanstack/react-router';
import type { QueryClient } from '@tanstack/react-query';
import { AppShell } from '@/components/layout/AppShell';
import { HomePage } from '@/pages/HomePage';
import { LoginPage } from '@/pages/LoginPage';
import { RadarPage } from '@/pages/radar/RadarPage';
import { ProjectsPage } from '@/pages/projects/ProjectsPage';
import { TrashPage } from '@/pages/projects/TrashPage';
import { WriterPage } from '@/pages/writer/WriterPage';
import { DirectorPage } from '@/pages/director/DirectorPage';
import { OpsPage } from '@/pages/ops/OpsPage';
import { BrainPage } from '@/pages/brain/BrainPage';
import { ComposePage } from '@/pages/compose/ComposePage';
import { TicketsPage } from '@/pages/tickets/TicketsPage';
import { AssetsPage } from '@/pages/assets/AssetsPage';
import { SettingsPage } from '@/pages/SettingsPage';
import { AdminPage } from '@/pages/admin/AdminPage';
import { AdminScriptsPage } from '@/pages/admin/AdminScriptsPage';
import { CrawlerAdminPage } from '@/pages/admin/CrawlerAdminPage';
import { IamPage } from '@/pages/admin/IamPage';
import { MembersPage } from '@/pages/admin/MembersPage';
import { useAuthStore } from '@/stores/auth';

interface RouterContext {
  queryClient: QueryClient;
}

export const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
});

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  validateSearch: (search: Record<string, unknown>) => ({
    redirect: typeof search.redirect === 'string' ? search.redirect : undefined,
  }),
  component: LoginPage,
});

const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  beforeLoad: ({ location }) => {
    const token = useAuthStore.getState().token;
    if (!token) {
      throw redirect({
        to: '/login',
        search: { redirect: location.href },
      });
    }
  },
  component: AppShell,
});

const indexRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  beforeLoad: () => {
    throw redirect({ to: '/radar' });
  },
  component: HomePage,
});

const radarRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/radar',
  component: RadarPage,
});

const projectsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/projects',
  component: ProjectsPage,
});

const trashRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trash',
  component: TrashPage,
});

const writerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/writer',
  component: WriterPage,
});

const writerEpisodeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/writer/$episodeId',
  component: WriterPage,
});

const directorRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/director',
  component: DirectorPage,
});

const directorEpisodeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/director/$episodeId',
  component: DirectorPage,
});

const opsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/ops',
  component: OpsPage,
});

const brainRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/brain',
  component: BrainPage,
});

const composeRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/compose',
  component: ComposePage,
});

const ticketsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tickets',
  component: TicketsPage,
});

const assetsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/assets',
  component: AssetsPage,
});

const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
});

const requireAdmin = () => {
  const role = useAuthStore.getState().user?.role;
  if (role !== 'SUPER_ADMIN' && role !== 'TENANT_ADMIN') {
    throw redirect({ to: '/radar' });
  }
};

const requireSuperAdmin = () => {
  const role = useAuthStore.getState().user?.role;
  if (role !== 'SUPER_ADMIN') {
    throw redirect({ to: '/radar' });
  }
};

const adminRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin',
  beforeLoad: requireAdmin,
  component: AdminPage,
});

const adminScriptsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/scripts',
  beforeLoad: requireAdmin,
  component: AdminScriptsPage,
});

const adminCrawlerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/scripts/radar-ipize',
  beforeLoad: requireAdmin,
  component: CrawlerAdminPage,
});

const adminIamRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/iam',
  beforeLoad: requireSuperAdmin,
  component: IamPage,
});

const iamMembersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/iam/members',
  beforeLoad: requireAdmin,
  component: MembersPage,
});

const authedTree = authedRoute.addChildren([
  indexRoute,
  radarRoute,
  projectsRoute,
  trashRoute,
  writerRoute,
  writerEpisodeRoute,
  directorRoute,
  directorEpisodeRoute,
  opsRoute,
  brainRoute,
  composeRoute,
  ticketsRoute,
  assetsRoute,
  settingsRoute,
  adminRoute,
  adminScriptsRoute,
  adminCrawlerRoute,
  adminIamRoute,
  iamMembersRoute,
]);

export const routeTree = rootRoute.addChildren([loginRoute, authedTree]);
